import React, { useMemo, useState } from 'react';
import { Form, InputNumber, Select, Typography, Alert, Collapse, Button, Space } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { currencyInputProps } from '../../../utils/numberInputMask';
import { simularCompararPrecosMercado, formatBRL } from '../../../utils/simuladores';
import SimuladorResultado from './SimuladorResultado';

const { Text, Paragraph } = Typography;

const UNIDADES = [
    {
        value: 'un',
        label: 'Unidade (un)',
        curta: 'un.',
        exemplo: 'Ex.: 1 sabonete vs pack com 3 · digite 1 e 3',
        qtdLabel: 'Quantidade (unidades)',
    },
    {
        value: 'g',
        label: 'Grama (g)',
        curta: 'g',
        exemplo: 'Ex.: café 250 g vs 500 g · digite 250 e 500',
        qtdLabel: 'Peso (gramas)',
    },
    {
        value: 'kg',
        label: 'Quilo (kg)',
        curta: 'kg',
        exemplo: 'Ex.: arroz 1 kg vs 5 kg · digite 1 e 5',
        qtdLabel: 'Peso (quilos)',
    },
    {
        value: 'ml',
        label: 'Mililitro (ml)',
        curta: 'ml',
        exemplo: 'Ex.: shampoo 200 ml vs 400 ml · digite 200 e 400',
        qtdLabel: 'Volume (mililitros)',
    },
    {
        value: 'L',
        label: 'Litro (L)',
        curta: 'L',
        exemplo: 'Ex.: óleo 0,9 L vs 1,5 L · digite 0,9 e 1,5',
        qtdLabel: 'Volume (litros)',
    },
];

const EMPTY_OPCAO = { preco: null, medida: null };

function CompararPrecosMercadoForm() {
    const [unidade, setUnidade] = useState('kg');
    const [opcoes, setOpcoes] = useState([{ ...EMPTY_OPCAO }, { ...EMPTY_OPCAO }]);
    const [diasParaAcabarMaior, setDiasParaAcabarMaior] = useState(null);
    const [validadeDias, setValidadeDias] = useState(null);

    const unidadeCfg = UNIDADES.find((u) => u.value === unidade) || UNIDADES[2];

    const resultado = useMemo(
        () =>
            simularCompararPrecosMercado({
                opcoes: opcoes.map((o, i) => ({
                    ...o,
                    label: `Opção ${i + 1}`,
                })),
                unidadeCurta: unidadeCfg.curta,
                diasParaAcabarMaior,
                validadeDias,
            }),
        [opcoes, unidadeCfg.curta, diasParaAcabarMaior, validadeDias]
    );

    const update = (index, field, value) => {
        setOpcoes((prev) => prev.map((o, i) => (i === index ? { ...o, [field]: value } : o)));
    };

    const addOpcao = () => {
        if (opcoes.length >= 3) return;
        setOpcoes((prev) => [...prev, { ...EMPTY_OPCAO }]);
    };

    const removeOpcao = (index) => {
        if (opcoes.length <= 2) return;
        setOpcoes((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="simulador-form">
            <Alert
                type="info"
                showIcon
                className="simulador-dica"
                message="Como usar"
                description="Serve para varejo vs atacado, tamanhos diferentes ou marcas. Escolha a unidade do rótulo e compare as opções — todas na mesma unidade."
                style={{ marginBottom: 16 }}
            />

            <Form layout="vertical">
                <Form.Item label="Em que unidade está o produto?" extra={unidadeCfg.exemplo}>
                    <Select
                        value={unidade}
                        onChange={setUnidade}
                        options={UNIDADES.map((u) => ({ value: u.value, label: u.label }))}
                        size="large"
                    />
                </Form.Item>

                {opcoes.map((opcao, index) => (
                    <div
                        className={`simulador-opcao-bloco${index === 1 ? ' simulador-opcao-bloco--destaque' : ''}`}
                        key={`opcao-${index}`}
                    >
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Text strong>Opção {index + 1}</Text>
                            {opcoes.length > 2 && (
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeOpcao(index)}
                                    aria-label={`Remover opção ${index + 1}`}
                                />
                            )}
                        </Space>
                        <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4, fontSize: 13 }}>
                            {index === 0
                                ? 'Ex.: embalagem menor, marca A…'
                                : index === 1
                                    ? 'Ex.: embalagem maior, outra marca…'
                                    : 'Ex.: terceira marca ou tamanho'}
                        </Paragraph>
                        <Form.Item label="Quanto custa?">
                            <InputNumber
                                {...currencyInputProps({ min: 0 })}
                                value={opcao.preco}
                                onChange={(v) => update(index, 'preco', v)}
                                size="large"
                            />
                        </Form.Item>
                        <Form.Item
                            label={unidadeCfg.qtdLabel}
                            extra={`Só o número do rótulo, em ${unidadeCfg.curta}`}
                        >
                            <InputNumber
                                min={0}
                                style={{ width: '100%' }}
                                decimalSeparator=","
                                inputMode="decimal"
                                suffix={` ${unidadeCfg.curta}`}
                                value={opcao.medida}
                                onChange={(v) => update(index, 'medida', v)}
                                size="large"
                                placeholder={unidade === 'kg' ? (index === 0 ? 'Ex.: 1' : 'Ex.: 5') : 'Ex.: 1'}
                            />
                        </Form.Item>
                    </div>
                ))}

                {opcoes.length < 3 && (
                    <Button
                        type="dashed"
                        block
                        icon={<PlusOutlined />}
                        onClick={addOpcao}
                        style={{ marginBottom: 16 }}
                    >
                        Comparar uma terceira opção
                    </Button>
                )}

                <Collapse
                    ghost
                    items={[{
                        key: 'desperdicio',
                        label: 'Será que o pacote grande estraga antes de acabar? (opcional)',
                        children: (
                            <>
                                <Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 13 }}>
                                    Só preencha se o produto vence (comida, iogurte…). Avisamos se a “economia” do pacote maior pode virar desperdício.
                                </Paragraph>
                                <Form.Item
                                    label="Em quantos dias acaba o pacote maior na sua casa?"
                                    extra="Pense no ritmo da sua casa, não precisa ser exato"
                                >
                                    <InputNumber
                                        min={1}
                                        max={3650}
                                        style={{ width: '100%' }}
                                        value={diasParaAcabarMaior}
                                        onChange={setDiasParaAcabarMaior}
                                        inputMode="numeric"
                                        size="large"
                                        suffix="dias"
                                        placeholder="Ex.: 30"
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Quantos dias de validade tem o produto?"
                                    extra="Olhe a data no rótulo ou estime (ex.: 15, 60, 180)"
                                >
                                    <InputNumber
                                        min={1}
                                        max={3650}
                                        style={{ width: '100%' }}
                                        value={validadeDias}
                                        onChange={setValidadeDias}
                                        inputMode="numeric"
                                        size="large"
                                        suffix="dias"
                                        placeholder="Ex.: 60"
                                    />
                                </Form.Item>
                            </>
                        ),
                    }]}
                />
            </Form>

            {resultado && (
                <SimuladorResultado
                    veredicto={resultado.veredicto}
                    stats={resultado.ranking.map((o) => ({
                        title: `${o.label} / ${unidadeCfg.curta}`,
                        value: formatBRL(o.precoPorUnidade),
                    }))}
                />
            )}
        </div>
    );
}

export default CompararPrecosMercadoForm;
